'use client';

import React, { useState, useEffect } from 'react';
import AppLayout from '@/components/AppLayout';
import { Phone, Clock, CheckCircle, Search, Loader2, RefreshCw, PhoneCall, Calendar, ChevronRight, X } from 'lucide-react';

interface UpcomingCallback {
  id: string;
  patientName: string;
  patientPhone: string;
  patientId: string;
  appointmentDate: string;
  appointmentTime: string;
  examNames: string[];
  confirmationStatus: 'unconfirmed' | 'confirmed' | 'cancelled' | 'rescheduled';
  operatorId?: string;
  operatorName?: string;
  callScript?: string;
  priority: 'normal' | 'urgent';
  notes?: string;
  lastContactAttempt?: string;
  contactAttempts: number;
}

const STATUS_CONFIG: Record<UpcomingCallback['confirmationStatus'], { label: string; color: string; bg: string }> = {
  unconfirmed: { label: 'Non confermato', color: 'text-amber-700', bg: 'bg-amber-100' },
  confirmed: { label: 'Confermato', color: 'text-green-700', bg: 'bg-green-100' },
  cancelled: { label: 'Annullato', color: 'text-red-700', bg: 'bg-red-100' },
  rescheduled: { label: 'Riprogrammato', color: 'text-blue-700', bg: 'bg-blue-100' },
};

export default function UpcomingCallbacksPage() {
  const [callbacks, setCallbacks] = useState<UpcomingCallback[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('unconfirmed');
  const [hoursFilter, setHoursFilter] = useState('48');
  const [selectedCallback, setSelectedCallback] = useState<UpcomingCallback | null>(null);
  const [calling, setCallingId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => { fetchCallbacks(); }, [hoursFilter]);

  const fetchCallbacks = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/callbacks/upcoming?hours=${hoursFilter}`);
      if (res.ok) setCallbacks(await res.json());
      else { console.error('[callbacks/upcoming] fetchCallbacks non-ok:', res.status); }
    } catch (err: unknown) {
      console.error('[callbacks/upcoming] fetchCallbacks error:', err);
      showToast('Errore caricamento callback');
    } finally { setLoading(false); }
  };

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  const handleCall = async (callback: UpcomingCallback) => {
    setCallingId(callback.id);
    try {
      await fetch('/api/twilio/outbound', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: callback.patientPhone, patientId: callback.patientId, callbackId: callback.id }),
      });
      await fetch(`/api/callbacks/upcoming/${callback.id}/attempt`, { method: 'POST' });
      setCallbacks(p => p.map(c => c.id === callback.id ? { ...c, contactAttempts: c.contactAttempts + 1, lastContactAttempt: new Date().toISOString() } : c));
      showToast(`Chiamata avviata a ${callback.patientName}`);
    } catch (err: unknown) {
      console.error('[callbacks/upcoming] handleCall error:', err);
      showToast('Errore avvio chiamata. Riprova.');
    } finally {
      setCallingId(null);
    }
  };

  const handleUpdateStatus = async (id: string, status: UpcomingCallback['confirmationStatus']) => {
    try {
      const res = await fetch(`/api/callbacks/upcoming/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirmationStatus: status }),
      });
      if (res.ok) {
        setCallbacks(p => p.map(c => c.id === id ? { ...c, confirmationStatus: status } : c));
        showToast(`Stato aggiornato: ${STATUS_CONFIG[status].label}`);
      } else {
        const j = await res.json().catch(() => ({}));
        showToast(j.error ?? 'Errore aggiornamento stato');
      }
    } catch (err: unknown) {
      console.error('[callbacks/upcoming] handleUpdateStatus error:', err);
      showToast('Errore di rete. Riprova.');
    }
  };

  const filtered = callbacks.filter(c => {
    const matchSearch = !search || c.patientName.toLowerCase().includes(search.toLowerCase()) || c.patientPhone.includes(search);
    const matchStatus = !statusFilter || c.confirmationStatus === statusFilter;
    return matchSearch && matchStatus;
  });

  const unconfirmedCount = callbacks.filter(c => c.confirmationStatus === 'unconfirmed').length;
  const urgentCount = callbacks.filter(c => c.priority === 'urgent').length;

  const hoursUntilAppointment = (dateStr: string, timeStr: string) => {
    const dt = new Date(`${dateStr}T${timeStr}`);
    const diff = (dt.getTime() - Date.now()) / (1000 * 60 * 60);
    return Math.max(0, Math.round(diff));
  };

  return (
    <AppLayout>
      <div className="p-6 max-w-6xl mx-auto">
        {toast && (
          <div className="fixed top-4 right-4 z-50 flex items-center gap-2 bg-green-600 text-white px-4 py-3 rounded-xl shadow-lg">
            <CheckCircle size={16} /><span className="text-sm font-medium">{toast}</span>
          </div>
        )}

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Phone size={24} className="text-primary" />
              Callback Futuri
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Appuntamenti prossimi da confermare telefonicamente</p>
          </div>
          <button onClick={fetchCallbacks} className="flex items-center gap-2 px-3 py-2 border border-border rounded-xl text-sm text-muted-foreground hover:bg-muted">
            <RefreshCw size={14} />Aggiorna
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[
            { label: 'Da confermare', value: unconfirmedCount, color: 'text-amber-600', bg: 'bg-amber-50' },
            { label: 'Urgenti', value: urgentCount, color: 'text-red-600', bg: 'bg-red-50' },
            { label: 'Totale prossimi', value: callbacks.length, color: 'text-blue-600', bg: 'bg-blue-50' },
          ].map(s => (
            <div key={s.label} className={`${s.bg} rounded-2xl p-4 border border-border`}>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-4 flex-wrap">
          <div className="relative flex-1 min-w-48">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input type="text" placeholder="Cerca paziente, telefono..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-9 pr-3 py-2 border border-border rounded-xl text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <select value={hoursFilter} onChange={e => setHoursFilter(e.target.value)} className="px-3 py-2 border border-border rounded-xl text-sm bg-background focus:outline-none">
            <option value="24">Prossime 24h</option>
            <option value="48">Prossime 48h</option>
            <option value="72">Prossimi 3 giorni</option>
            <option value="168">Prossima settimana</option>
          </select>
          <div className="flex gap-2">
            {['', 'unconfirmed', 'confirmed', 'cancelled'].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)} className={`px-3 py-2 rounded-xl text-sm font-medium border transition-colors ${statusFilter === s ? 'bg-primary text-white border-primary' : 'border-border text-muted-foreground hover:bg-muted'}`}>
                {s === '' ? 'Tutti' : STATUS_CONFIG[s as UpcomingCallback['confirmationStatus']]?.label}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 size={32} className="animate-spin text-primary" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <Phone size={48} className="mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium">Nessun callback da effettuare</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(callback => {
              const hoursLeft = hoursUntilAppointment(callback.appointmentDate, callback.appointmentTime);
              return (
                <div key={callback.id} className={`bg-white dark:bg-card border rounded-2xl p-4 ${callback.priority === 'urgent' ? 'border-red-200' : 'border-border'}`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${hoursLeft <= 24 ? 'bg-red-100' : hoursLeft <= 48 ? 'bg-amber-100' : 'bg-blue-100'}`}>
                      <Clock size={20} className={hoursLeft <= 24 ? 'text-red-600' : hoursLeft <= 48 ? 'text-amber-600' : 'text-blue-600'} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-foreground">{callback.patientName}</p>
                        {callback.priority === 'urgent' && (
                          <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">URGENTE</span>
                        )}
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CONFIG[callback.confirmationStatus].bg} ${STATUS_CONFIG[callback.confirmationStatus].color}`}>
                          {STATUS_CONFIG[callback.confirmationStatus].label}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground flex-wrap">
                        <span className="flex items-center gap-1"><Phone size={11} />{callback.patientPhone}</span>
                        <span className="flex items-center gap-1"><Calendar size={11} />{new Date(callback.appointmentDate).toLocaleDateString('it-IT')} {callback.appointmentTime}</span>
                        <span className={`font-medium ${hoursLeft <= 24 ? 'text-red-600' : hoursLeft <= 48 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                          {hoursLeft}h al appuntamento
                        </span>
                        {callback.contactAttempts > 0 && (
                          <span>{callback.contactAttempts} tentativo/i</span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {callback.examNames.map(e => <span key={e} className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">{e}</span>)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {callback.confirmationStatus === 'unconfirmed' && (
                        <>
                          <button
                            onClick={() => handleCall(callback)}
                            disabled={calling === callback.id}
                            className="flex items-center gap-1.5 px-3 py-2 bg-primary text-white rounded-xl text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
                          >
                            {calling === callback.id ? <Loader2 size={14} className="animate-spin" /> : <PhoneCall size={14} />}
                            Chiama
                          </button>
                          <button onClick={() => handleUpdateStatus(callback.id, 'confirmed')} className="px-3 py-2 bg-green-100 text-green-700 rounded-xl text-sm font-medium hover:bg-green-200">
                            <CheckCircle size={14} />
                          </button>
                          <button onClick={() => handleUpdateStatus(callback.id, 'cancelled')} className="px-3 py-2 bg-red-100 text-red-700 rounded-xl text-sm font-medium hover:bg-red-200">
                            <X size={14} />
                          </button>
                        </>
                      )}
                      <button onClick={() => setSelectedCallback(callback)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground">
                        <ChevronRight size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
